import { useEffect, useRef } from 'react';
import { openChatStream } from '../lib/sse';
import { useChatStore } from '../store/chat';
import type { AgentEvent } from '../types/agent';

// O sessionId vive no store: "nova conversa" gera outro, e reabrir uma conversa
// arquivada restaura o antigo (contexto do gateway continua enquanto o processo viver).
function getSessionId(): string {
  return useChatStore.getState().sessionId;
}

export function useChatStream() {
  const pendingUserMessageId = useChatStore((s) => s.pendingUserMessageId);
  const messages = useChatStore((s) => s.messages);
  const controllerRef = useRef<AbortController | null>(null);
  const lastDispatchedIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!pendingUserMessageId) return;
    if (lastDispatchedIdRef.current === pendingUserMessageId) return;
    lastDispatchedIdRef.current = pendingUserMessageId;

    const userMsg = messages.find((m) => m.id === pendingUserMessageId);
    if (!userMsg) return;

    // Abort any prior in-flight stream before opening a new one (RESEARCH Pitfall 1).
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;

    let firstEventReceived = false;
    const store = useChatStore.getState();

    const dispatchEvent = (event: AgentEvent) => {
      // Per D-15: first SSE event of ANY kind advances tick sending -> delivered.
      if (!firstEventReceived) {
        firstEventReceived = true;
        store.setDelivered();
      }
      switch (event.kind) {
        case 'text_chunk':
          store.appendTextChunk(event.text);
          break;
        case 'tool_call':
          store.setToolStatus(event.name);
          break;
        case 'tool_result':
          store.consumeToolResult();
          break;
        case 'ui_flow':
          store.attachUiPayload(event.payload);
          break;
        case 'price_warning':
          store.attachPriceWarning(event.warning);
          break;
        case 'pix_qr':
          store.attachPixPayload(event.payload);
          break;
        case 'lead_captured':
          // No visual rendering — bot text bubble handles LEAD-04 confirmation [D-03]
          break;
        case 'quick_replies':
          store.attachQuickReplies(event.labels);
          break;
        case 'cotacao':
          store.attachCotacao(event.cotacao);
          break;
        case 'oferta_alt':
          store.attachOfertaAlt(event.oferta);
          break;
        case 'done':
          store.markDone();
          break;
      }
    };

    openChatStream(
      userMsg.outgoing ?? userMsg.text,
      getSessionId(),
      {
        onOpen: () => store.setConnectionState('streaming'),
        onEvent: dispatchEvent,
        onClose: () => {
          if (useChatStore.getState().pendingUserMessageId === pendingUserMessageId) {
            store.markDone();
          }
        },
        onError: () => {
          store.setConnectionState('error');
        },
      },
      controller.signal
    );
  }, [pendingUserMessageId, messages]);

  useEffect(() => {
    return () => {
      controllerRef.current?.abort();
    };
  }, []);
}
